/** The placeholder for a cell with nothing to show, shared by the table and the PDF. */
export const NO_VALUE = "—";

/**
 * A measurement with its unit, or the dash. Zero counts as nothing to show: a route with no
 * distance, no speed or no fuel carries no information, and a column of `0 km` reads as measured
 * data rather than the gap it actually is.
 *
 * Display only — the Excel twin keeps the raw number so the sheet still sorts and sums.
 */
export function formatMetric(value: number | null | undefined, unit: string): string {
  if (value == null || !Number.isFinite(value) || value === 0) return NO_VALUE;
  return `${value} ${unit}`;
}

/**
 * Average consumption in litres per 100 km, to one decimal.
 *
 * Undefined rather than zero whenever either side is missing: a route with no measured distance
 * has no consumption to state, and dividing by it would read as a measurement rather than a gap.
 */
export function consumptionPer100Km(
  fuelLitres: number | null | undefined,
  distanceKm: number | null | undefined,
): number | undefined {
  if (fuelLitres == null || distanceKm == null) return undefined;
  if (!Number.isFinite(fuelLitres) || !Number.isFinite(distanceKm)) return undefined;
  if (fuelLitres <= 0 || distanceKm <= 0) return undefined;
  return Math.round((fuelLitres / distanceKm) * 1000) / 10;
}

/**
 * `8.3`, or the dash. Always one decimal, so a column of them lines up.
 *
 * `unit` is optional and omitted on screen: the column is headed "average consumption", which
 * already states L/100km. Exports pass it, because a bare number in a spreadsheet does not.
 */
export function formatConsumption(
  fuelLitres: number | null | undefined,
  distanceKm: number | null | undefined,
  unit?: string,
): string {
  const value = consumptionPer100Km(fuelLitres, distanceKm);
  if (value === undefined) return NO_VALUE;
  return unit ? `${value.toFixed(1)} ${unit}` : value.toFixed(1);
}

/**
 * A fuel-fill amount with its sign and unit — `+30 L`.
 *
 * Rounded to whole litres. The figure is a tank-level delta, and the sub-litre digits a level
 * sensor reports are noise rather than measurement: on a route that is still open upstream
 * recomputes the delta every request, so the decimals visibly twitch between reloads. A fill small
 * enough to round to zero falls back to the two decimals both callers already round to, so the
 * amount never reads `+0 L`.
 */
export function formatRefuelAmount(litres: number, unit: string): string {
  const whole = Math.round(litres);
  const shown = whole === 0 ? Math.round(litres * 100) / 100 : whole;
  return `+${shown} ${unit}`;
}
